import React, { useEffect, useMemo } from 'react';

/**
 * Polymorphic full-screen alert / status overlay.
 *
 * @param {keyof JSX.IntrinsicElements | React.ElementType} [as='div'] — Root element for the inner panel.
 * @param {boolean} open
 * @param {() => void} [onClose]
 * @param {'success'|'error'|'warning'|'info'|'loading'} [variant='info']
 * @param {string} [title]
 * @param {React.ReactNode} [message] — Shown when `children` is omitted.
 * @param {React.ReactNode} [children]
 * @param {boolean} [closeOnOverlayClick=true] — Ignored while `variant === 'loading'`.
 * @param {boolean} [showCloseButton] — Defaults to false for loading, true otherwise.
 * @param {number|null} [autoCloseMs] — Auto-invoke `onClose` after this many ms (when > 0).
 * @param {boolean} [closeOnEscape=true] — Ignored while `variant === 'loading'`.
 * @param {number} [zIndex=10000]
 */
const PolymorphicAlertOverlay = ({
  as: Panel = 'div',
  open,
  onClose,
  variant = 'info',
  title,
  message,
  children,
  closeOnOverlayClick = true,
  showCloseButton,
  autoCloseMs = null,
  closeOnEscape = true,
  zIndex = 10000,
}) => {
  const content = children != null ? children : message;

  const isLoading = variant === 'loading';
  const allowDismiss =
    !isLoading && typeof onClose === 'function';
  const overlayDismiss =
    allowDismiss && closeOnOverlayClick;
  const escapeDismiss =
    allowDismiss && closeOnEscape;
  const showX =
    showCloseButton !== undefined
      ? showCloseButton && allowDismiss
      : allowDismiss;

  useEffect(() => {
    if (!open || !autoCloseMs || autoCloseMs <= 0 || !onClose) return undefined;
    const t = setTimeout(() => onClose(), autoCloseMs);
    return () => clearTimeout(t);
  }, [open, autoCloseMs, onClose]);

  useEffect(() => {
    if (!open || !escapeDismiss) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, escapeDismiss, onClose]);

  const theme = useMemo(() => {
    switch (variant) {
      case 'success':
        return {
          accent: '#059669',
          accentSoft: '#ecfdf5',
          border: '#a7f3d0',
          icon: '✓',
        };
      case 'error':
        return {
          accent: '#dc2626',
          accentSoft: '#fef2f2',
          border: '#fecaca',
          icon: '✕',
        };
      case 'warning':
        return {
          accent: '#d97706',
          accentSoft: '#fffbeb',
          border: '#fde68a',
          icon: '!',
        };
      case 'loading':
        return {
          accent: '#2563eb',
          accentSoft: '#eff6ff',
          border: '#bfdbfe',
          icon: null,
        };
      default:
        return {
          accent: '#2563eb',
          accentSoft: '#eff6ff',
          border: '#bfdbfe',
          icon: 'i',
        };
    }
  }, [variant]);

  if (!open) return null;

  const handleBackdropClick = () => {
    if (overlayDismiss) onClose();
  };

  const stop = (e) => e.stopPropagation();

  return (
    <>
      <style>{`
        @keyframes polymorphic-alert-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div
        role="presentation"
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          backgroundColor: 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <Panel
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={title ? 'polymorphic-alert-title' : undefined}
          aria-describedby="polymorphic-alert-desc"
          onClick={stop}
          style={{
            position: 'relative',
            maxWidth: 440,
            width: '100%',
            borderRadius: 14,
            padding: '28px 24px 24px',
            backgroundColor: '#fff',
            boxShadow: '0 20px 50px rgba(15, 23, 42, 0.2)',
            border: `1px solid ${theme.border}`,
          }}
        >
          {showX && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 36,
                height: 36,
                border: 'none',
                borderRadius: 8,
                background: 'transparent',
                color: '#64748b',
                fontSize: 22,
                lineHeight: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </button>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 16,
            }}
          >
            {isLoading ? (
              <div
                style={{
                  width: 52,
                  height: 52,
                  border: '4px solid #e2e8f0',
                  borderTopColor: theme.accent,
                  borderRadius: '50%',
                  animation: 'polymorphic-alert-spin 0.85s linear infinite',
                }}
              />
            ) : (
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: theme.accentSoft,
                  border: `2px solid ${theme.border}`,
                  color: theme.accent,
                  fontSize: 28,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {theme.icon}
              </div>
            )}

            {title ? (
              <h2
                id="polymorphic-alert-title"
                style={{
                  margin: 0,
                  fontSize: '1.15rem',
                  fontWeight: 700,
                  color: '#0f172a',
                }}
              >
                {title}
              </h2>
            ) : null}

            <div
              id="polymorphic-alert-desc"
              style={{
                margin: 0,
                fontSize: '1rem',
                fontWeight: 600,
                color: isLoading ? theme.accent : '#334155',
                lineHeight: 1.5,
              }}
            >
              {content}
            </div>
          </div>
        </Panel>
      </div>
    </>
  );
};

export default PolymorphicAlertOverlay;
